import { inject, Injectable } from '@angular/core';
import { CtBatch } from '@ps-gui/claims-terminal-shared';
import { DialogService } from 'primeng/dynamicdialog';
import { filter, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { BaseWorkflowHookService } from '../base-workflow-hook.service';
import { AppView, DocumentView, isDocumentView, isExtractionView } from '../../../models/app-view';
import {
  showDivergingSumDialogHook,
  showDocumentExtractionSummaryHook,
  useExtractedDataForTrainingConfirmationHook,
} from './proof-after-document-hooks';
import { PersistDataService } from '../../app-state/persist-data.service';
import { squeezeMap } from '../../../utils/Rxjs.utils';
import { ExtractedDataStoreService } from '../../extracted-data/extracted-data-store.service';
import { calculateSumOfCurrencyColumnInCents } from '../../../utils/Table.utils';
import { toCents } from '../../../utils/Money.utils';

@Injectable({ providedIn: 'root' })
export class ProofWorkflowHookService extends BaseWorkflowHookService {
  private readonly dialogService = inject(DialogService);
  private readonly extractedDataStoreService = inject(ExtractedDataStoreService);
  private readonly persistDataService = inject(PersistDataService);

  override afterView(view: AppView, nextView: AppView, ctBatch: CtBatch): Observable<boolean> {
    console.debug('ProofWorkflowHookService.afterView', view, ctBatch);
    if (!isDocumentView(view)) return of(true);

    if (isExtractionView(view) && view.page.sections[0].key === 'dokumentendaten') {
      return from(this.saveExtractionSnapshot(view)).pipe(map(() => true));
    }

    const divergingSumDialogHook = this.getSuggestedSumDialogHook(view);

    if (isDocumentView(nextView) && view.documentId === nextView.documentId) {
      return divergingSumDialogHook;
    }

    const extractionSummaryHook = () => {
      return showDocumentExtractionSummaryHook(
        view.documentId,
        view.documentClass,
        this.dialogService,
        this.extractedDataStoreService,
      ).pipe(
        filter(({ accept }) => accept),
        squeezeMap(() => this.saveExtractionSnapshot(view)),
        switchMap(({ useExtractedDataForTraining }) => {
          // We only save the shouldUseForTraining flag in the following two cases:
          // * The user ticks the checkbox and confirms the follow-up dialog (we save true)
          // * The user unchecks the checkbox and continues (we save false)
          return useExtractedDataForTraining
            ? useExtractedDataForTrainingConfirmationHook(this.dialogService).pipe(
                filter((shouldUse) => shouldUse),
                tap((shouldUse) =>
                  this.persistDataService.setUseTrainingDataForDocument(view.documentId, shouldUse),
                ),
              )
            : of(true).pipe(
                tap(() =>
                  this.persistDataService.setUseTrainingDataForDocument(view.documentId, false),
                ),
              );
        }),
      );
    };

    return divergingSumDialogHook.pipe(switchMap(() => extractionSummaryHook()));
  }

  private saveExtractionSnapshot(view: DocumentView): Promise<void> {
    const snapshot = this.extractedDataStoreService.getExtractedDocumentDataSnapshot(
      view.documentId,
    );

    return this.persistDataService.saveExtractedDocumentDataSnapshot(
      view.documentId,
      view.documentClass,
      snapshot,
    );
  }

  private getSuggestedSumDialogHook(view: DocumentView): Observable<boolean> {
    const savedSuggestedSum = this.extractedDataStoreService.getExtractedBatchFields(
      view.documentId,
    )['kopfdaten_gesamtbetrag'];

    if (
      savedSuggestedSum === undefined ||
      !isExtractionView(view) ||
      !view.page.sections.some((section) => section.key === 'forderungen')
    ) {
      return of(true);
    }

    const savedSumInCents = toCents(Number(savedSuggestedSum));

    return from(this.extractedDataStoreService.getExtractedTables(view.documentId)).pipe(
      map((tables) => tables['forderungen'] ?? []),
      map((rows) =>
        // A bit ugly, but the most simple way to just skip the dialog if there are no
        // extracted rows (anymore), but there is still saved value for the suggested sum.
        rows.length > 0
          ? calculateSumOfCurrencyColumnInCents(rows, 'forderungen_betrag')
          : savedSumInCents,
      ),
      switchMap((sumInCents) =>
        savedSuggestedSum !== null && savedSumInCents !== sumInCents
          ? showDivergingSumDialogHook(this.dialogService)
          : of(true),
      ),
    );
  }
}
